import { isEmpty, omit } from 'lodash-es';

import {
	ICatalogSource,
	ICatalogSourceCapability,
	MetadataFilter,
	MetadataFilterPayload,
	MetadataFilterType,
	MetadataOptionChange,
	ProfilingFilter,
} from './catalog-source-model';

export type CSRunResponse = object;
export type ScheduleConfig = object;

const CAPABILITY_NAME_METADATA_EXTRACTION = 'Metadata Extraction';
const CAPABILITY_NAME_PROFILING = 'Data Profiling';
const METADATA_EXTRACTION_FILTER_CONFIG = 'Metadata Filter Config';
const PROFILING_OPTION_NAME = 'Data Profiling Scope';
export type ConnectionDetail = {
	srcConnection: string;
	stagingConnection: string;
	runtimeEnv: string;
};
type CatalogSourceConfig = {
	connectionDetails: ConnectionDetail;
	csName: string;
};
type Capabilities = {
	enableProfiling?: boolean;
	enableDataQuality?: boolean;
};

export enum SupportedCatalogSourceType {
	Oracle = 'Oracle',
	warehouse = 'Fabric Data Warehouse',
}

export abstract class CatalogSource {
	abstract getType(): SupportedCatalogSourceType;
	abstract getTypeOptionGroupName(): string;
	private _commonFilters: Array<string> = [];
	private _config!: CatalogSourceConfig;

	constructor(config: CatalogSourceConfig | ICatalogSource) {
		if ('connectionDetails' in config) {
			this._config = config;
		} else {
			const csName = config.name as string;

			//Read ConnectionId from configuration
			const connectionDetails: CatalogSourceConfig['connectionDetails'] = {
				srcConnection: '',
				stagingConnection: '',
				runtimeEnv: '',
			};
			if (config.typeOptions) {
				const connectionConfig = config.typeOptions.configurationProperties
					?.find((config) => config.optionGroupName === this.getTypeOptionGroupName())
					?.configOptions?.find((option) => option.key === 'ConnectionId');

				if (connectionConfig && connectionConfig.values) {
					connectionDetails.srcConnection = connectionConfig.values[0];
				}
			}

			if (config.typeCapabilities) {
				const connectionConfig = config.typeCapabilities
					.find((config) => config.capabilityName === CAPABILITY_NAME_PROFILING)
					?.configurationProperties?.find((config) => config.optionGroupName === PROFILING_OPTION_NAME);

				if (connectionConfig) {
					const staging = connectionConfig?.configOptions?.find((option) => option.key === 'Staging Connection');
					if (staging && staging.values) {
						connectionDetails.stagingConnection = staging.values[0];
					}
					const runtime = connectionConfig?.configOptions?.find((option) => option.key === 'Elastic Runtime Environment');
					if (runtime && runtime.values) {
						connectionDetails.runtimeEnv = runtime.values[0];
					}
				}
			}

			this._config = {
				connectionDetails,
				csName,
			};

			//Reading all the filters from the configuration
			if (config.typeCapabilities) {
				const metadataExtractionConfig = config.typeCapabilities.find((config) => config.capabilityName === CAPABILITY_NAME_METADATA_EXTRACTION);
				if (metadataExtractionConfig && metadataExtractionConfig.configurationProperties) {
					metadataExtractionConfig.configurationProperties.forEach((config) => {
						if (config.optionGroupName === METADATA_EXTRACTION_FILTER_CONFIG) {
							const metadataFilter = new MetadataFilter(config.configOptions as MetadataFilterPayload);
							this.addFilter(metadataFilter.config.value);
						}
					});
				}
			}
		}
	}

	private getName() {
		return this._config.csName;
	}
	private getGlobalConfigOptions(filters?: string[]) {
		let filterValue = ['No Filters'];
		if (filters && !isEmpty(filters)) {
			filterValue = ['Filter Conditions'];
		}
		const config = [
			{
				globalConfigurationName: 'Configuration',
				configurationProperties: [
					{
						optionGroupName: 'Metadata and Data Filters',
						configOptions: [
							{
								key: 'Filter Options',
								values: [...filterValue],
							},
						],
					},
				],
			},
			{
				globalConfigurationName: 'Execution',
				configurationProperties: [
					{
						optionGroupName: 'Runtime Settings',
						configOptions: [
							{
								key: 'Runtime Environment',
								values: [this._config.connectionDetails.runtimeEnv],
							},
						],
					},
					{
						optionGroupName: 'Metadata Sync Settings',
						configOptions: [
							{
								key: 'Mode of Execution',
								values: ['Full Sync'],
							},
						],
					},
				],
			},
		];

		return config;
	}
	private getCSPayload(
		metadataOptionChange: MetadataOptionChange,
		enableCapabilities: Capabilities,
		filters?: Array<string>,
		filterType: MetadataFilterType = 'Tables',
	): ICatalogSource {
		const { enableDataQuality = false, enableProfiling = false } = enableCapabilities;
		const typeCapabilities: ICatalogSourceCapability[] = [this.getMetadataExtactionConfig(filterType, filters)];
		if (enableProfiling && filters) {
			typeCapabilities.push(this.getProfilingConfig(filterType, filters));
		}
		if (enableDataQuality) {
			typeCapabilities.push(this.getDataQualityConfig());
		}
		return {
			name: this.getName(),
			description: `${this.getType()} catalog source created for fabric integration`,
			type: this.getType(),
			custom: false,
			typeOptions: {
				configurationProperties: [
					{
						optionGroupName: this.getTypeOptionGroupName(),
						configOptions: [
							{
								key: 'ConnectionId',
								values: [this._config.connectionDetails.srcConnection],
							},
						],
					},
				],
			},
			capabilities: this.getCapabilities(metadataOptionChange, { enableProfiling, enableDataQuality }),
			globalConfigOptions: this.getGlobalConfigOptions(filters),
			typeCapabilities,
		};
	}

	get getConnectionDetails() {
		return this._config.connectionDetails;
	}
	public hasFilter(criteria: string): boolean {
		return this._commonFilters.includes(criteria);
	}

	public addFilter(criteria: string): void {
		if (!this.hasFilter(criteria)) {
			this._commonFilters.push(criteria);
		}
	}

	public getFilters() {
		return this._commonFilters;
	}

	private getCapabilities(metadataOptionChange: MetadataOptionChange, enableCapabilities: { enableDataQuality: boolean; enableProfiling: boolean }) {
		const { enableDataQuality, enableProfiling } = enableCapabilities;
		const metadataExtraction = {
			capabilityName: 'Metadata Extraction',
			configurationProperties: [
				{
					optionGroupName: 'Metadata Extraction OptionGroup',
					configOptions: [
						{
							key: 'Metadata Change Option',
							values: [metadataOptionChange],
						},
						{
							key: 'Type of scan',
							values: ['Full'],
						},
					],
				},
			],
		};
		const profiling = {
			capabilityName: 'Data Profiling',
			configurationProperties: [
				{
					optionGroupName: 'Data Profiling Scope',
					configOptions: [
						{
							key: 'Modes of Run',
							values: ['Keep Signatures and Values'],
						},
					],
				},
			],
		};
		const dq = {
			capabilityName: 'Data Quality',
			configurationProperties: [
				{
					optionGroupName: 'Data Quality Scope',
					configOptions: [
						{
							key: 'Data Quality Automation',
							values: ['true'],
							additionalMetadata: null,
						},
						{
							key: 'Data Quality Automation Scope',
							values: ['Apply on all Data Elements'],
						},
						{
							key: 'Data Quality Run Scope',
							values: ['Full'],
							additionalMetadata: null,
						},
						{
							key: 'Cache Result',
							values: ['Agent Cache'],
							additionalMetadata: null,
						},
					],
				},
			],
		};
		const capabilities = [metadataExtraction];
		if (enableProfiling) {
			capabilities.push(profiling);
		}
		if (enableDataQuality) {
			capabilities.push(dq);
		}
		return capabilities;
	}

	private convertToMetadataFilter(filterType: MetadataFilterType, value: string) {
		return new MetadataFilter({
			type: filterType,
			pivot: 'Include Metadata',
			value,
		});
	}

	private convertToProfilingFilter(filterType: MetadataFilterType, value: string) {
		return new ProfilingFilter({
			type: filterType,
			pivot: 'Include Metadata',
			conditions: 'Named',
			value,
		});
	}

	private getMetadataExtactionConfig(filterType: MetadataFilterType, filters?: Array<string>): ICatalogSourceCapability {
		const filtersToAdd = filters && filters.length > 0 ? filters : this._commonFilters;
		return {
			capabilityName: CAPABILITY_NAME_METADATA_EXTRACTION,
			configurationProperties: [
				...filtersToAdd.map((filter) => ({
					optionGroupName: METADATA_EXTRACTION_FILTER_CONFIG,
					configOptions: this.convertToMetadataFilter(filterType, filter).toPayload(),
				})),
				{
					optionGroupName: 'Scanner Configuration Options',
					configOptions: [],
				},

				{
					optionGroupName: 'Additional settings',
					configOptions: [],
				},
			],
		};
	}

	private getDataQualityConfig(): ICatalogSourceCapability {
		return {
			capabilityName: 'Data Quality',
			configurationProperties: [
				{
					optionGroupName: 'Data Quality Scope',
					configOptions: [
						{
							key: 'Sampling Type',
							values: ['Limit N Rows'],
						},
						{
							key: 'No of rows to limit',
							values: [1000],
						},
						{
							key: 'Maximum Precision of String Fields',
							values: ['50'],
						},
						{
							key: 'Text Qualifier',
							values: ['Double Quote'],
						},
						{
							key: 'Runtime Environment',
							values: [this._config.connectionDetails.runtimeEnv],
						},
					],
				},
			],
		};
	}

	private getProfilingConfig(filterType: MetadataFilterType, filters?: string[]): ICatalogSourceCapability {
		//NOTE:: We will be using the same filter as the metadata filter so explicit filter is not needed for now.
		const filtersToAdd = filters && filters.length > 0 ? filters : this._commonFilters;

		return {
			capabilityName: 'Data Profiling',
			configurationProperties: [
				{
					optionGroupName: 'Data Profiling Scope',
					configOptions: [
						{
							key: 'Profiling Run Scope',
							values: ['Incremental'],
						},
						{
							key: 'Sampling Type',
							values: ['Limit N Rows'],
						},
						{
							key: 'No of rows to limit',
							values: ['10000'],
						},
						{
							key: 'Maximum Precision of String Fields',
							values: ['50'],
						},
						{
							key: 'Runtime Environment',
							values: [this._config.connectionDetails.runtimeEnv],
						},
						{
							key: 'Text Qualifier',
							values: ['Double Quote'],
						},
					],
				},
				...filtersToAdd.map((filter) => ({
					optionGroupName: 'Profiling Filter Config',
					configOptions: this.convertToProfilingFilter(filterType, filter).toPayload(),
				})),
			],
		};
	}

	public getSyncProfilingPayload(criteria: string, filterType?: MetadataFilterType) {
		const exludeProperties = ['name', 'description', 'type', 'custom'];
		const payload = this.getCSPayload('Retain', { enableProfiling: true }, criteria ? [criteria] : undefined, filterType);
		return omit(payload, exludeProperties);
	}

	public getCreatePayload() {
		return this.getCSPayload('Delete', { enableProfiling: true }, []);
	}
}
